import React from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactGA from 'react-ga4'
import PrimaryButton from '../../../../components/PrimaryButton'
import { CircularProgress } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Bounce, toast } from 'react-toastify'
import { useAuth } from '../../../../utils/AuthContext/AuthProvider'
import { AuthService } from '../../../../utils/api'


const validationSchema = Yup.object({
  name: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email format').required('Required'),
  password: Yup.string().required('Required'),
})

interface SignUpFormProps {
  handleLogin: () => void
}

const SignUpForm: React.FC<SignUpFormProps> = ({
  handleLogin,
}) => {
  const [isLoading, setIsLoading] = React.useState(false)
  const { signUp, isLoading: ProfileLoading } = useAuth()
  const navigate = useNavigate()


  const formik = useFormik({
    initialValues: {
      name: '',
      lastName: '',
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true)
      ReactGA.event({
        category: 'RP_BTN_SIGN_UP',
        action: 'RP_BTN_SIGN_UP',
        label: 'RP_BTN_SIGN_UP',
      })
      try {
        await signUp(values)
        setIsLoading(false)
      } catch (error: any) {
        setIsLoading(false)
        let errorMessage = 'An error occurred during sign-up. Please try again.'
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        }
        toast.error(errorMessage, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
          transition: Bounce,
        })
      }
    },
  })

  if (ProfileLoading) return <CircularProgress size={24} />

  const verifyEmail = async () => {
    const data = await AuthService.verifyEmail(formik.values.email ?? '')
    await formik.setFieldTouched('email', true)
    if (data) {
      formik.setFieldError('email', 'Email already exists')
    }
  }

  return (
    <form className={'gap-4 flex sm:max-w-[364px] w-full flex-col'} onSubmit={formik.handleSubmit}>
      <div onClick={() => {
        navigate(-1)
      }}
           className={' text-[15px] cursor-pointer items-center justify-start max-sm:hidden  flex flex-row gap-2 leading-[20px] text-start text-[#737373] font-Inter'}>
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path d='M12.6665 7.99999H3.33317M3.33317 7.99999L7.99984 3.33333M3.33317 7.99999L7.99984 12.6667'
                stroke='#737373' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
        Back
      </div>
      <div
        className={'font-semibold font-Inter sm:text-[28px] text-[20px]  pb-1 sm:leading-[32px] leading-[24px] max-sm:pt-4 max-sm:pb-5 text-[#0F0F0F] text-start'}>
        Create Account
      </div>
      <div className={'flex flex-row gap-4'}>
        <div className={'flex flex-col flex-start flex-1'}>
          <div className={'font-Inter text-start pb-2 font-[500] text-[14px]'}>
            First Name
          </div>
          <input
            id='name'
            name='name'
            type='text'
            placeholder='Name'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.name}
            className={' rounded-[8px] !border-[1px] !h-[44px] !border-[#CCCCCC] focus:!outline-[#000] focus:!border-[1px] px-[10px] py-[14px] w-full'}
          />
          {formik.touched.name && formik.errors.name ? (
            <div className={'text-red-500 text-[12px]  text-start'}>{formik.errors.name}</div>
          ) : null}
        </div>
        <div className={' flex flex-col flex-start  flex-1'}>
          <div className={'font-Inter text-start pb-2 font-[500] text-[14px]'}>
            Last Name
          </div>
          <input
            id='lastName'
            name='lastName'
            type='text'
            placeholder='Last Name'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.lastName}
            className={' rounded-[8px] !border-[1px] !h-[44px] !border-[#CCCCCC] focus:!outline-[#000] focus:!border-[1px] px-[10px] py-[14px] w-full'}
          />
          {formik.touched.lastName && formik.errors.lastName ? (
            <div className={'text-red-500 text-[12px] text-start'}>{formik.errors.lastName}</div>
          ) : null}
        </div>
      </div>
      <div>
        <div className={'font-Inter text-start pb-2 font-[500] text-[14px]'}>
          Email
        </div>
        <input
          id='email'
          name='email'
          type='email'
          placeholder='Email'
          onChange={formik.handleChange}
          onBlur={verifyEmail}
          value={formik.values.email}
          className={' rounded-[8px] !border-[1px] !h-[44px] !border-[#CCCCCC] focus:!outline-[#000] focus:!border-[1px] px-[10px] py-[14px] w-full'}
        />
        {formik.touched.email && formik.errors.email ? (
          <div className={'text-red-500 text-[12px] text-start'}>{formik.errors.email}</div>
        ) : null}
      </div>
      <div>
        <div className={'font-Inter text-start pb-2 font-[500] text-[14px]'}>
          Password
        </div>
        <input
          id='password'
          name='password'
          type='password'
          placeholder='Password'
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.password}
          className={' rounded-[8px] !border-[1px] !h-[44px] !border-[#CCCCCC] focus:!outline-[#000] focus:!border-[1px] px-[10px] py-[14px] w-full'}
        />
        {formik.touched.password && formik.errors.password ? (
          <div className={'text-red-500 text-[12px] text-start'}>{formik.errors.password}</div>
        ) : null}
      </div>
      <span className={' text-[15px] text-start leading-[20px] text-[#737373] font-Inter'}>
          By creating account, you agree to Pyrpose`s <span onClick={() => {
        navigate('/terms-of-use', { replace: false })
      }}
                                                            className={'underline cursor-pointer underline-offset-2 text-[#525252]'}>Terms of Use</span> and <span
        onClick={() => {
          navigate('/privacy-policy', { replace: false })
        }
        }
        className={'underline cursor-pointer underline-offset-2 text-[#525252]'}>Privacy Policy</span>
        </span>
      <PrimaryButton
        onClick={() => formik.handleSubmit()}
        disabled={isLoading}
        className=' !bg-[#37992E] !w-full h-[48px] !my-[16px] rounded-xl justify-center items-center gap-2 inline-flex'
      >
        {formik.isSubmitting ? (<CircularProgress size={24} />) :
          <div className='text-center text-white !text-[17px] font-semibold !font-Inter normal-case '>Continue</div>
        }
      </PrimaryButton>
      <span className={' text-[15px] leading-[20px] text-start text-[#737373] font-Inter'}>
          Already have an account?  <span onClick={handleLogin}
                                          className={'underline cursor-pointer underline-offset-2 text-start text-[#525252]'}>Log in instead</span>
        </span>
    </form>
  )
}

export default SignUpForm